<script setup>
import toast from 'dashboard/helper/toast';
import { messageTimestamp } from 'shared/helpers/timeHelper';
import { useStoreGetters, useStore } from 'dashboard/composables/store';
import TableFooter from 'dashboard/components/widgets/TableFooter.vue';
import PageLayout from 'dashboard/components-next/layout/PageLayout.vue';
import PageHeader from 'dashboard/components-next/layout/PageHeader.vue';
import ComboBox from 'dashboard/components-next/combobox/ComboBox.vue';
import Input from 'dashboard/components-next/input/Input.vue';
import { useDebounceFn } from '@vueuse/core';
import {
  generateTranslationPayload,
  generateLogActionKey,
} from 'dashboard/helper/auditlogHelper';
import { computed, onMounted, ref, watch } from 'vue';
import { useI18n } from 'vue-i18n';
import { useRoute, useRouter } from 'vue-router';

const getters = useStoreGetters();
const store = useStore();
const router = useRouter();
const records = computed(() => getters['auditlogs/getAuditLogs'].value);
const uiFlags = computed(() => getters['auditlogs/getUIFlags'].value);
const meta = computed(() => getters['auditlogs/getMeta'].value);
const agentList = computed(() => getters['agents/getAgents'].value);
const customRoles = computed(() => getters['customRole/getCustomRoles'].value);

const { t } = useI18n();
const route = useRoute();

// Página e filtros moram na URL: recarregar ou compartilhar o link mantém o recorte.
const routerPage = computed(() => Number(route.query.page ?? 1));
const filterUserId = computed(() => route.query.user_id ?? '');
const filterCategory = computed(() => route.query.category ?? '');
const filterQuery = computed(() => route.query.q ?? '');
const hasFilters = computed(
  () =>
    Boolean(filterUserId.value) ||
    Boolean(filterCategory.value) ||
    Boolean(filterQuery.value)
);

// Texto digitado vive local e só vai pra URL depois da pausa — cada tecla
// virando request/entrada no histórico do navegador seria ruído.
const searchText = ref(filterQuery.value);

// Mesmas chaves do AuditLogs::FilterService::CATEGORIES (backend).
const CATEGORY_KEYS = [
  'agents',
  'teams',
  'inboxes',
  'sessions',
  'automations',
  'webhooks',
  'conversations',
  'account',
  'vision',
  'kanban',
];

const userOptions = computed(() => [
  { value: '', label: t('AUDIT_LOGS.FILTERS.ALL_USERS') },
  ...agentList.value.map(agent => ({
    value: String(agent.id),
    label: agent.email ? `${agent.name} (${agent.email})` : agent.name,
  })),
]);

const categoryOptions = computed(() => [
  { value: '', label: t('AUDIT_LOGS.FILTERS.ALL_CATEGORIES') },
  ...CATEGORY_KEYS.map(key => ({
    value: key,
    label: t(`AUDIT_LOGS.FILTERS.CATEGORIES.${key.toUpperCase()}`),
  })),
]);

const fetchAuditLogs = async () => {
  try {
    await store.dispatch('auditlogs/fetch', {
      page: routerPage.value,
      userId: filterUserId.value,
      category: filterCategory.value,
      query: filterQuery.value,
    });
  } catch (error) {
    toast.error(error?.message || t('AUDIT_LOGS.API.ERROR_MESSAGE'));
  }
};

// Trocar filtro volta pra página 1 — a página 40 do recorte anterior não existe no novo.
const updateFilters = changes => {
  const query = {
    user_id: filterUserId.value,
    category: filterCategory.value,
    q: filterQuery.value,
    ...changes,
    page: 1,
  };
  Object.keys(query).forEach(key => {
    if (query[key] === '' || query[key] === null) delete query[key];
  });
  router.push({ name: 'auditlogs_list', query });
};

const generateLogText = auditLogItem => {
  const payload = generateTranslationPayload(
    auditLogItem,
    agentList.value,
    customRoles.value
  );
  const translationKey = generateLogActionKey(auditLogItem);

  const joinIfArray = value => {
    return Array.isArray(value) ? value.join(', ') : value;
  };

  const mergedPayload = {
    ...payload,
    attributes: joinIfArray(payload.attributes),
    values: joinIfArray(payload.values),
  };
  return t(translationKey, mergedPayload);
};

const applySearch = useDebounceFn(() => {
  updateFilters({ q: searchText.value.trim() });
}, 500);

watch(filterQuery, value => {
  if (value !== searchText.value.trim()) searchText.value = value;
});

const onPageChange = page => {
  router.push({ name: 'auditlogs_list', query: { ...route.query, page } });
};

onMounted(() => {
  store.dispatch('agents/get');
  store.dispatch('customRole/getCustomRole');
  fetchAuditLogs();
});

watch(
  () => route.query,
  (newQuery, oldQuery) => {
    if (JSON.stringify(newQuery) !== JSON.stringify(oldQuery)) {
      fetchAuditLogs();
    }
  }
);

const tableHeaders = computed(() => {
  return [
    t('AUDIT_LOGS.LIST.TABLE_HEADER.ACTIVITY'),
    t('AUDIT_LOGS.LIST.TABLE_HEADER.TIME'),
    t('AUDIT_LOGS.LIST.TABLE_HEADER.IP_ADDRESS'),
  ];
});
</script>

<template>
  <PageLayout>
    <template #header>
      <PageHeader
        icon="i-lucide-history"
        accent="slate"
        :title="$t('AUDIT_LOGS.HEADER')"
        :subtitle="$t('AUDIT_LOGS.DESCRIPTION')"
      />
    </template>

    <div class="flex flex-wrap gap-3 mb-4">
      <div class="w-72 max-w-full">
        <Input
          v-model="searchText"
          type="search"
          :placeholder="$t('AUDIT_LOGS.FILTERS.SEARCH_PLACEHOLDER')"
          custom-input-class="!ps-9"
          @input="applySearch"
        >
          <template #prefix>
            <span
              class="absolute z-10 -translate-y-1/2 pointer-events-none i-lucide-search size-4 top-1/2 start-3 text-n-slate-10"
            />
          </template>
        </Input>
      </div>
      <div class="w-80 max-w-full">
        <ComboBox
          :model-value="filterUserId"
          :options="userOptions"
          :placeholder="$t('AUDIT_LOGS.FILTERS.USER_PLACEHOLDER')"
          :search-placeholder="$t('AUDIT_LOGS.FILTERS.USER_SEARCH')"
          @update:model-value="value => updateFilters({ user_id: value })"
        />
      </div>
      <div class="w-56 max-w-full">
        <ComboBox
          :model-value="filterCategory"
          :options="categoryOptions"
          :placeholder="$t('AUDIT_LOGS.FILTERS.CATEGORY_PLACEHOLDER')"
          @update:model-value="value => updateFilters({ category: value })"
        />
      </div>
    </div>

    <div class="text-n-slate-11">
      <woot-loading-state
        v-if="uiFlags.fetchingList"
        :message="$t('AUDIT_LOGS.LOADING')"
      />
      <p
        v-else-if="!records.length"
        class="flex flex-col items-center justify-center h-full text-base p-8"
      >
        {{
          hasFilters ? $t('AUDIT_LOGS.SEARCH_404') : $t('AUDIT_LOGS.LIST.404')
        }}
      </p>
      <div v-else class="min-w-full overflow-x-auto">
        <table class="divide-y divide-n-weak">
          <thead>
            <th
              v-for="thHeader in tableHeaders"
              :key="thHeader"
              class="py-4 ltr:pr-4 rtl:pl-4 text-left font-semibold text-n-slate-11"
            >
              {{ thHeader }}
            </th>
          </thead>
          <tbody class="divide-y divide-n-weak text-n-slate-11">
            <tr v-for="auditLogItem in records" :key="auditLogItem.id">
              <td class="py-4 ltr:pr-4 rtl:pl-4 break-all whitespace-nowrap">
                {{ generateLogText(auditLogItem) }}
              </td>
              <td class="py-4 ltr:pr-4 rtl:pl-4 break-all whitespace-nowrap">
                {{
                  messageTimestamp(
                    auditLogItem.created_at,
                    'MMM dd, yyyy hh:mm a'
                  )
                }}
              </td>
              <td class="py-4 w-[8.75rem]">
                {{ auditLogItem.remote_address }}
              </td>
            </tr>
          </tbody>
        </table>
        <TableFooter
          :current-page="Number(meta.currentPage)"
          :total-count="meta.totalEntries"
          :page-size="meta.perPage"
          class="border-n-weak border-t !px-0 py-4"
          @page-change="onPageChange"
        />
      </div>
    </div>
  </PageLayout>
</template>
